import styled from '@emotion/styled';
import { FileReference } from '@kaizenplatform/belfast-entity';
import { Chip, Typography } from '@material-ui/core';
import { ClassAttributes, createElement, Fragment } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';

import ModificationRequestEditing from '../../../../entity/ModificationRequestEditing';
import UploadingFile from '../../../../entity/UploadingFile';
import {
  DeliverableForCraetionDetailFragment,
  ModificationRequestForDetailFragment,
} from '../../../../generated/graphql';
import { toJSDate } from '../../../../graphql/datetime';
import theme from '../../../../theme';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import LfToBr from '../../primitive/LfToBr';
import _Item from './Item';

interface Props {
  modificationRequestExpired: boolean;
  modificationRequest: ModificationRequestForDetailFragment;
  modificationRequestEditing: ModificationRequestEditing;
  deliverable?: DeliverableForCraetionDetailFragment | null;
  assetFiles: Array<FileReference>;
  onUploadIssueAssetFiles: (files: UploadingFile[], bucketId: string, itemId: string) => void;
  onUploadIssueCommentAssetFiles: (files: UploadingFile[], deliverableId: string, issueId: string) => void;
  onRemoveAssetIssueFile: (bucketId: string, fileId: string, issueId: string) => void;
  onRemoveAssetIssueCommentFile: (bucketId: string, fileId: string, issueId: string) => void;
  onSubmitModificationRequestIssueComment: (issueId: string, comment: string) => void;
  onRemoveAllAssetFile: () => void;
  onSubmitModificationRequestIssue: (
    modificationRequestItemId: string,
    inputComment: string,
    bucketId: string | null,
    files: Array<FileReference>,
  ) => Promise<void>;
  onRequestResolveModificationRequestIssue: (id: string) => void;
  onRequestReopenModificationRequestIssue: (id: string) => void;
  className?: string;
}

const ModificationRequestDetail = ({
  modificationRequestExpired,
  modificationRequest,
  modificationRequestEditing,
  deliverable,
  assetFiles,
  onUploadIssueAssetFiles,
  onUploadIssueCommentAssetFiles,
  onRemoveAssetIssueFile,
  onRemoveAssetIssueCommentFile,
  onRemoveAllAssetFile,
  onSubmitModificationRequestIssue,
  onRequestResolveModificationRequestIssue,
  onRequestReopenModificationRequestIssue,
  onSubmitModificationRequestIssueComment,
  className,
}: ClassAttributes<HTMLElement> & Props) => {
  const items = modificationRequest.items.map((item, i) => (
    <Item
      item={item}
      modificationRequestExpired={modificationRequestExpired}
      modificationRequestState={modificationRequest.state}
      modificationRequestEditing={modificationRequestEditing}
      bucketId={modificationRequest.bucketId || null}
      deliverable={deliverable}
      files={assetFiles}
      onUploadIssueAssetFiles={onUploadIssueAssetFiles}
      onUploadIssueCommentAssetFiles={onUploadIssueCommentAssetFiles}
      onRemoveAssetIssueFile={onRemoveAssetIssueFile}
      onRemoveAssetIssueCommentFile={onRemoveAssetIssueCommentFile}
      onSubmitModificationRequestIssueComment={onSubmitModificationRequestIssueComment}
      onRemoveAllAssetFile={onRemoveAllAssetFile}
      onSubmitModificationRequestIssue={onSubmitModificationRequestIssue}
      onRequestResolveModificationRequestIssue={onRequestResolveModificationRequestIssue}
      onRequestReopenModificationRequestIssue={onRequestReopenModificationRequestIssue}
      index={i}
      key={`modificationRequestItem-${i}`}
    />
  ));

  return (
    <div className={className}>
      {modificationRequest.state === 'given_feedback' ? (
        <FeedbackSection>
          <Typography gutterBottom={true} style={{ color: '#f9a825' }}>
            <FormattedMessage id="modification_request_state.given_feedback_detail" />
          </Typography>

          {modificationRequest.feedback ? (
            <>
              <SectionHeading>
                <FormattedMessage id="modification_request_detail.feedback_reason" />
              </SectionHeading>
              <ReasonText gutterBottom={true}>
                <LfToBr text={modificationRequest.feedback.body} />
              </ReasonText>
            </>
          ) : null}
        </FeedbackSection>
      ) : null}

      <Chips>
        <Chip
          label={
            <>
              <FormattedMessage id="modification_request.detail_card.chips.fee_type" />:
              <FormattedMessage id={`modification_request.detail_card.chips.${modificationRequest.feeType}`} />
            </>
          }
          variant="outlined"
        />

        {modificationRequest.submittedAt ? (
          <FormattedDate value={toJSDate(modificationRequest.submittedAt)} year="numeric" month="2-digit" day="2-digit">
            {(date: string) => (
              <Chip
                label={
                  <>
                    <FormattedMessage id="modification_request.detail_card.chips.created_at" />: {date}
                  </>
                }
                variant="outlined"
              />
            )}
          </FormattedDate>
        ) : null}

        {modificationRequest.deliveryDue ? (
          <FormattedDate value={toJSDate(modificationRequest.deliveryDue)} year="numeric" month="2-digit" day="2-digit">
            {(date: string) => (
              <Chip
                label={
                  <>
                    <FormattedMessage id="modification_request.detail_card.chips.delivery_due" />: {date}
                  </>
                }
                variant="outlined"
              />
            )}
          </FormattedDate>
        ) : null}
      </Chips>

      <Items>{items}</Items>
    </div>
  );
};

const FeedbackSection = styled('section')({
  margin: `${SPACING_UNIT * 3}px 0`,
  padding: SPACING_REGULAR,
  border: `1px solid ${theme.palette.divider}`,
});

const SectionHeading = styled(Typography)({
  marginBottom: SPACING_UNIT,
  fontWeight: 700,
});

const Chips = styled('div')(
  flexibleLayoutContainer({
    alignItems: 'center',
  }),
  {
    marginBottom: SPACING_REGULAR,
    '& > *': {
      marginRight: SPACING_UNIT,
    },
  },
);

const Items = styled('div')(flexibleLayoutContainer({ direction: 'column' }));

const Item = styled(_Item)({
  marginBottom: SPACING_REGULAR,
  ':last-child': {
    marginBottom: 0,
  },
});

const ReasonText = styled(Typography)({
  whiteSpace: 'initial',
  wordBreak: 'break-word',
});

export default ModificationRequestDetail;
